// Global
import React, { useEffect, useState } from 'react';
import { Field, RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import { RichTextProps } from '@sitecore-jss/sitecore-jss-nextjs/types/components/RichText';

//Local
import useExperienceEditor from 'lib/use-experience-editor';

import { hostToReplace } from 'lib/host-to-replace';

const srOnlySpan = '<span class="sr-only"> (Opens in a new tab)</span>';

const RichTextA11yWrapper = ({
  field,
  className = '',
  editable = true,
  ...props
}: RichTextProps): JSX.Element => {
  /**
   * Hook for experience editor
   */
  const isExperienceEditor = useExperienceEditor();

  const [content, setContent] = useState(field?.value);

  const updatedField: Field<string> = {
    value: content || '',
  };

  // Run this client-side because we don't have access to the document server-side
  useEffect(() => {
    const template = document.createElement('template');
    template.innerHTML = field?.value || '';

    const Images = [...template.content.querySelectorAll('img')].filter((img) =>
      img.attributes.getNamedItem('src')?.value.startsWith('http')
    );

    Images.forEach((img) => {
      const url = hostToReplace({ value: { href: img?.src } });
      img.setAttribute('src', url?.value?.href as string);
      img.innerHTML = `${img.innerHTML}${srOnlySpan}`;
    });

    // Find all links either either have target="_blank" or appear to be external due to starting with "http"
    const externalLinks = [...template.content.querySelectorAll('a')].filter(
      (a) =>
        a.attributes.getNamedItem('href')?.value.startsWith('http') ||
        a.attributes.getNamedItem('target')?.value === '_blank'
    );
    // Update each external link
    externalLinks.forEach((a) => {
      const replaceValue = hostToReplace({ value: { href: a?.href } });

      a.setAttribute('href', replaceValue?.value?.href as string);

      // Set to open in new tab
      a.setAttribute('target', '_blank');
      // Add Screen Reader text and new tab icon
      // eslint-disable-next-line no-param-reassign
      a.innerHTML = `${a.innerHTML}${srOnlySpan}`;
    });

    // Update the content
    setContent(template.innerHTML);
  }, [field?.value]);

  // If there's no text, the only render it if it is editable and we are editing.
  // Otherwise don't render anything
  if (!field?.value) {
    if (isExperienceEditor && editable) {
      return <RichText field={field} {...props} editable={editable} className={className} />;
    }
    return <></>;
  }

  // Just a pass through if it's EE.
  if (isExperienceEditor) {
    return <RichText field={field} {...props} editable={editable} className={className} />;
  }
  /**
   * If not EE, use the updated field value
   */
  return <RichText field={updatedField} {...props} editable={editable} className={className} />;
};

export default RichTextA11yWrapper;
